// ProfessionalCandidatePage.js
import React, { useState, useEffect } from 'react';
import { Form, Pagination, Button, Card, Row, Col, Badge } from 'react-bootstrap';
import {
    FaSearch,
    FaEye,
    FaMapMarkerAlt,
    FaClock,
    FaStar,
    FaHandshake,
    FaFilter,
    FaTimes,
    FaCity,
    FaGlobe,
    FaHome,
    FaBriefcase,
    FaSearchPlus,
    FaTools,
    FaSync,
    FaUsers
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ProfessionalService from '../../services/ProfessionalService';
import Banner from "../../components/partials/Banner";
import AuthService from '../../services/AuthService';
import '../../assets/css/ProfessionalManagement.css';
import ProfessionalCandidateDetailModal from "../../components/modals/professionalCandidate/ProfessionalCandidateDetailModal";
import ClientEOICreateModal from "../../components/modals/client/ClientEOICreateModal";

// Fallback générique si aucune photo n'est disponible (SVG inline : ne dépend
// d'aucun service externe et ne peut jamais se retrouver "cassé").
const DEFAULT_AVATAR =
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23e9ecef'/%3E%3Ccircle cx='150' cy='115' r='55' fill='%23ced4da'/%3E%3Cellipse cx='150' cy='265' rx='95' ry='75' fill='%23ced4da'/%3E%3C/svg%3E";

// Dimensions fixes de la vignette photo
const CARD_IMAGE_HEIGHT = 220;

const ProfessionalCandidatePage = () => {
    const [professionals, setProfessionals] = useState([]);
    const [filteredProfessionals, setFilteredProfessionals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInterestModal, setShowInterestModal] = useState(false);
    const [selectedProfessionalForContact, setSelectedProfessionalForContact] = useState(null);

    // États pour les filtres
    const [filters, setFilters] = useState({
        city: '',
        country: '',
        neighborhood: '',
        service: '',
        yearsOfExperience: '',
        languages: '',
        availabilityStatus: 'all'
    });

    // États pour les options de filtre
    const [filterOptions, setFilterOptions] = useState({
        cities: [],
        countries: [],
        neighborhoods: [],
        services: [],
        languages: []
    });

    const [showFilters, setShowFilters] = useState(false);
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);

    // États pour la Lightbox
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);

    const itemsPerPage = 6;

    // ── Résout la meilleure URL de photo disponible pour un professionnel ──────
    const resolveProfilePhoto = (professional) => {
        if (professional.profilePhotoUrlPresigned) {
            return professional.profilePhotoUrlPresigned;
        }

        if (professional.profilePhotoUrl) {
            if (professional.profilePhotoUrl.startsWith('http')) {
                return professional.profilePhotoUrl;
            }
            return `${process.env.REACT_APP_MINIO_URL || ''}/${professional.profilePhotoUrl}`;
        }

        if (professional.user?.profilePictureUrl) {
            return professional.user.profilePictureUrl;
        }

        return DEFAULT_AVATAR;
    };

    // Ouvre la photo de profil en plein écran avec zoom
    const handleOpenPhotoPreview = (url) => {
        setLightboxSlides([{ src: url }]);
        setLightboxOpen(true);
    };

    useEffect(() => {
        fetchValidatedProfessionals();
    }, []);

    useEffect(() => {
        if (professionals.length > 0) {
            extractFilterOptions();
        }
    }, [professionals]);

    useEffect(() => {
        applyFilters();
        updateActiveFiltersCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, searchTerm, professionals]);

    const extractFilterOptions = () => {
        const cities = new Set();
        const countries = new Set();
        const neighborhoods = new Set();
        const services = new Set();
        const languages = new Set();

        professionals.forEach(professional => {
            if (professional.city) cities.add(professional.city);
            if (professional.country) countries.add(professional.country);
            if (professional.neighborhood) neighborhoods.add(professional.neighborhood);
            if (professional.selectedService) services.add(professional.selectedService);

            if (professional.languages) {
                const langList = professional.languages.split(/[,;]/).map(lang => lang.trim()).filter(Boolean);
                langList.forEach(lang => languages.add(lang));
            }
        });

        setFilterOptions({
            cities: Array.from(cities).sort(),
            countries: Array.from(countries).sort(),
            neighborhoods: Array.from(neighborhoods).sort(),
            services: Array.from(services).sort(),
            languages: Array.from(languages).sort()
        });
    };

    const applyFilters = () => {
        let result = [...professionals];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(professional => {
                const fullName = `${professional.firstName} ${professional.lastName}`.toLowerCase();
                const email = professional.email?.toLowerCase() || '';
                const service = professional.selectedService?.toLowerCase() || '';
                const city = professional.city?.toLowerCase() || '';
                const neighborhood = professional.neighborhood?.toLowerCase() || '';
                const country = professional.country?.toLowerCase() || '';
                const about = professional.about?.toLowerCase() || '';

                return fullName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    service.includes(searchLower) ||
                    city.includes(searchLower) ||
                    neighborhood.includes(searchLower) ||
                    country.includes(searchLower) ||
                    about.includes(searchLower);
            });
        }

        if (filters.city) {
            result = result.filter(professional =>
                professional.city && professional.city.toLowerCase() === filters.city.toLowerCase()
            );
        }

        if (filters.country) {
            result = result.filter(professional =>
                professional.country && professional.country.toLowerCase() === filters.country.toLowerCase()
            );
        }

        if (filters.neighborhood) {
            result = result.filter(professional =>
                professional.neighborhood && professional.neighborhood.toLowerCase() === filters.neighborhood.toLowerCase()
            );
        }

        if (filters.service) {
            result = result.filter(professional =>
                professional.selectedService && professional.selectedService.toLowerCase() === filters.service.toLowerCase()
            );
        }

        if (filters.yearsOfExperience) {
            const minExperience = parseInt(filters.yearsOfExperience);
            result = result.filter(professional =>
                professional.yearsOfExperience >= minExperience
            );
        }

        if (filters.languages) {
            result = result.filter(professional =>
                professional.languages && professional.languages.toLowerCase().includes(filters.languages.toLowerCase())
            );
        }

        if (filters.availabilityStatus !== 'all') {
            const isAvailable = filters.availabilityStatus === 'available';
            result = result.filter(professional =>
                professional.availabilityStatus === isAvailable
            );
        }

        setFilteredProfessionals(result);
        setCurrentPage(1);
    };

    const updateActiveFiltersCount = () => {
        let count = 0;
        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key] !== 'all') {
                count++;
            }
        });
        setActiveFiltersCount(count);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const resetFilters = () => {
        setFilters({
            city: '',
            country: '',
            neighborhood: '',
            service: '',
            yearsOfExperience: '',
            languages: '',
            availabilityStatus: 'all'
        });
        setSearchTerm('');
        setShowFilters(false);
    };

    const fetchValidatedProfessionals = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const professionalData = await ProfessionalService.getAllAcceptedAndAvailableProfessionals();

            if (!professionalData || professionalData.length === 0) {
                setProfessionals([]);
                return;
            }

            const formattedProfessionals = professionalData.map(professional => {
                const user = professional.user || {};

                return {
                    ...professional,

                    // Informations de base
                    id: professional.id || user.id,
                    firstName: user.firstName || 'Non spécifié',
                    lastName: user.lastName || 'Non spécifié',
                    email: user.email || 'Non spécifié',
                    phoneNumber: user.phoneNumber || 'Non spécifié',
                    neighborhood: user.neighborhood || 'Non spécifié',
                    city: user.city || professional.city || 'Non spécifié',
                    country: user.country || 'Non spécifié',

                    // Informations professionnelles
                    serviceId: professional.serviceId,
                    selectedService: professional.selectedService || 'Service non spécifié',
                    subServices: professional.subServices || '',
                    yearsOfExperience: professional.yearsOfExperience || 0,
                    totalYearsOfExperience: professional.yearsOfExperience || 0,
                    about: professional.about || user.additionalInfos || 'Aucune description disponible',
                    languages: professional.languages || '',
                    availabilityStatus: professional.availabilityStatus !== undefined ? professional.availabilityStatus : true,
                    status: professional.status || 'ACCEPTED',

                    // URL de la photo
                    profilePhotoUrl: professional.profilePhotoUrl,
                    profilePhotoUrlPresigned: professional.profilePhotoUrlPresigned,

                    // Autres informations
                    desiredContractLength: professional.desiredContractDuration || professional.desiredContractLength || null,
                    desiredContractCities: professional.desiredContractCities || '',
                    additionalInfos: professional.about || user.additionalInfos || 'Aucune information supplémentaire',
                    enabled: user.enabled !== undefined ? user.enabled : true,
                    accountNonLocked: user.accountNonLocked !== undefined ? user.accountNonLocked : true,
                    accountNonExpired: user.accountNonExpired !== undefined ? user.accountNonExpired : true,
                    credentialsNonExpired: user.credentialsNonExpired !== undefined ? user.credentialsNonExpired : true,
                    roles: user.roles || [],
                    dateOfBirth: user.dateOfBirth || 'Non spécifié',
                    gender: user.gender || 'Non spécifié',
                    useRef: user.useRef || professional.useRef || 'Non spécifié',
                    documentUrl: professional.documentFileUrl || professional.documentUrl,
                    documentType: professional.documentType || 'CV',
                    selfiePhotoUrl: professional.profilePhotoUrl || user.profilePictureUrl,
                    address: professional.address || '',
                    desiredStartDate: professional.desiredStartDate || '',
                    desiredContractDuration: professional.desiredContractDuration || 0,
                    totalNumOfContracts: professional.totalNumOfContracts || 0,
                    userValidated: professional.userValidated || '',
                    validationDate: professional.validationDate || '',
                    identification: professional.identification,
                    criminalRecord: professional.criminalRecord,

                    // URL d'affichage résolue pour la carte
                    displayPhotoUrl: resolveProfilePhoto(professional),
                };
            });

            setProfessionals(formattedProfessionals);

        } catch (err) {
            console.error('Erreur détaillée:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du chargement des professionnels validés';
            setError(errorMessage);
            Swal.fire('Erreur !', 'Impossible de charger les professionnels validés. Veuillez réessayer.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowInterest = (professional) => {
        const currentUser = AuthService.getCurrentUser();

        if (!currentUser) {
            Swal.fire('Connexion requise', 'Vous devez être connecté pour contacter un professionnel.', 'warning');
            return;
        }

        if (!professional.serviceId) {
            Swal.fire('Information manquante', 'Ce professionnel n\'a pas de service défini.', 'warning');
            return;
        }

        setSelectedProfessionalForContact(professional);
        setShowInterestModal(true);
    };

    const handleInterestSuccess = () => {
        // Placeholder pour une éventuelle action de suivi après succès
    };

    const getServiceLabel = (service) => {
        if (!service) return 'Service non spécifié';

        const serviceMap = {
            'services_de_mnage': 'Services de Ménage',
            'plomberie': 'Plomberie',
            'electricite': 'Électricité',
            'peinture': 'Peinture',
            'menuiserie': 'Menuiserie',
            'maçonnerie': 'Maçonnerie',
            'SERVICE SUR MESURE': 'Service sur mesure'
        };

        return serviceMap[service] || service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'ACCEPTED':
                return <Badge className="status-badge status-accepted">Validé</Badge>;
            case 'PENDING':
                return <Badge className="status-badge status-pending">En attente</Badge>;
            case 'REJECTED':
                return <Badge className="status-badge status-rejected">Rejeté</Badge>;
            default:
                return <Badge bg="secondary">Inconnu</Badge>;
        }
    };

    const handleViewDetails = (professional) => {
        setSelectedProfessional(professional);
        setIsModalOpen(true);
    };

    // Fonction pour formater et afficher les sous-services avec un meilleur label
    const renderSubServices = (subServicesString) => {
        if (!subServicesString) return null;

        const subServices = subServicesString.split(',').map(s => s.trim());
        if (subServices.length === 0) return null;

        const displayServices = subServices.slice(0, 2);
        const remainingCount = subServices.length - 2;

        return (
            <div className="professional-meta">
                <FaTools />
                <span>
                    <strong>Compétences :</strong> {displayServices.join(', ')}
                    {remainingCount > 0 && (
                        <span className="text-muted"> +{remainingCount} autres</span>
                    )}
                </span>
            </div>
        );
    };

    const totalPages = Math.ceil(filteredProfessionals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentProfessionals = filteredProfessionals.slice(startIndex, startIndex + itemsPerPage);

    // La disponibilité a déjà son propre état visuel dans la barre de filtres
    // rapides (chip active/inactive) : inutile de la répéter ici.
    const activeFilterDefs = [
        filters.city && { key: 'city', label: `Ville : ${filters.city}`, onClear: () => handleFilterChange('city', '') },
        filters.country && { key: 'country', label: `Pays : ${filters.country}`, onClear: () => handleFilterChange('country', '') },
        filters.neighborhood && { key: 'neighborhood', label: `Quartier : ${filters.neighborhood}`, onClear: () => handleFilterChange('neighborhood', '') },
        filters.service && { key: 'service', label: `Service : ${getServiceLabel(filters.service)}`, onClear: () => handleFilterChange('service', '') },
        filters.yearsOfExperience && { key: 'experience', label: `Expérience : ${filters.yearsOfExperience}+ ans`, onClear: () => handleFilterChange('yearsOfExperience', '') },
        filters.languages && { key: 'languages', label: `Langue : ${filters.languages}`, onClear: () => handleFilterChange('languages', '') },
        searchTerm && { key: 'search', label: `Recherche : ${searchTerm}`, onClear: () => setSearchTerm('') },
    ].filter(Boolean);

    // Filtres rapides de disponibilité, rendus comme les puces "pm-filter-chip"
    // déjà utilisées sur la page d'administration, pour un rendu cohérent.
    const quickAvailabilityFilters = [
        { key: 'all', label: 'Tous' }
    ];

    // Nombre de filtres "avancés" actifs (hors disponibilité, déjà visible
    // dans la barre de filtres rapides, et hors recherche texte).
    const advancedFilterKeys = ['city', 'country', 'neighborhood', 'service', 'yearsOfExperience', 'languages'];
    const advancedFiltersCount = advancedFilterKeys.filter(key => filters[key]).length;

    const renderActiveFilters = () => {
        if (activeFilterDefs.length === 0) return null;

        return (
            <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                <small className="text-muted">Filtres actifs :</small>
                {activeFilterDefs.map(f => (
                    <span key={f.key} className="pc-active-chip">
                        {f.label}
                        <FaTimes onClick={f.onClear} />
                    </span>
                ))}
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="rounded-pill"
                    onClick={resetFilters}
                >
                    <FaTimes className="me-1" /> Tout effacer
                </Button>
            </div>
        );
    };

    return (
        <>
            <HeaderTopSection />
            <Banner title="Liste des Professionnels Validés" />

            <div className="professional-management-container bg-light">
                <div className="container py-4">

                    {/* ===== HEADER (même rendu que la page d'administration) ===== */}
                    <div className="pm-header rounded-4 p-4 p-md-5 mb-4 position-relative overflow-hidden">
                        <FaUsers className="pm-header-decoration" aria-hidden="true" />

                        <div className="pm-header-content position-relative">
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                                <div>
                                    <h1 className="text-white fw-bold pm-header-title mb-2">
                                        🔍 Trouver un Professionnel
                                    </h1>
                                    <p className="text-white-50 mb-0">
                                        Parcourez les professionnels validés et disponibles près de chez vous
                                    </p>
                                </div>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="pm-refresh-btn rounded-pill px-4 align-self-start align-self-lg-auto"
                                    onClick={fetchValidatedProfessionals}
                                >
                                    <FaSync className="me-1" /> Actualiser
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ===== RECHERCHE ===== */}
                    <div className="mb-4">
                        <div className="pm-search-bar d-flex align-items-center">
                            <span className="pm-search-icon ps-3 pe-2">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Rechercher par nom, service, ville ou quartier..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pm-search-input border-0 shadow-none py-3"
                            />
                            {searchTerm && (
                                <Button
                                    variant="link"
                                    className="text-muted p-2"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <FaTimes />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ===== FILTRES ===== */}
                    <div className="mb-4">
                        <div className="pm-filter-bar d-flex flex-wrap gap-2 p-2 bg-white rounded-4 border">
                            {quickAvailabilityFilters.map(f => {
                                const isActive = filters.availabilityStatus === f.key;
                                return (
                                    <button
                                        key={f.key}
                                        type="button"
                                        className={`pm-filter-chip btn ${isActive ? 'pm-filter-chip-active' : ''}`}
                                        onClick={() => handleFilterChange('availabilityStatus', f.key)}
                                    >
                                        {f.label}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                className={`pm-filter-chip btn d-flex align-items-center ${showFilters ? 'pm-filter-chip-active' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <FaFilter className="me-1" size={12} /> Filtres avancés
                                {advancedFiltersCount > 0 && (
                                    <Badge
                                        bg={showFilters ? 'light' : 'secondary'}
                                        text={showFilters ? 'dark' : 'white'}
                                        className="ms-1 rounded-pill"
                                    >
                                        {advancedFiltersCount}
                                    </Badge>
                                )}
                            </button>
                        </div>

                        {renderActiveFilters()}

                        {showFilters && (
                            <Card className="pc-advanced-filters mb-4">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-semibold">
                                        <FaFilter className="me-2 text-primary" />
                                        Filtres avancés
                                    </h6>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={resetFilters}
                                        className="text-danger p-0 text-decoration-none"
                                    >
                                        <FaTimes className="me-1" />
                                        Réinitialiser
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaCity className="me-2 text-primary" />
                                                    Ville
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.city}
                                                    onChange={(e) => handleFilterChange('city', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Toutes les villes</option>
                                                    {filterOptions.cities.map(city => (
                                                        <option key={city} value={city}>{city}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaGlobe className="me-2 text-primary" />
                                                    Pays
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.country}
                                                    onChange={(e) => handleFilterChange('country', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Tous les pays</option>
                                                    {filterOptions.countries.map(country => (
                                                        <option key={country} value={country}>{country}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaHome className="me-2 text-primary" />
                                                    Quartier
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.neighborhood}
                                                    onChange={(e) => handleFilterChange('neighborhood', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Tous les quartiers</option>
                                                    {filterOptions.neighborhoods.map(neighborhood => (
                                                        <option key={neighborhood} value={neighborhood}>{neighborhood}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaBriefcase className="me-2 text-primary" />
                                                    Service
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.service}
                                                    onChange={(e) => handleFilterChange('service', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Tous les services</option>
                                                    {filterOptions.services.map(service => (
                                                        <option key={service} value={service}>{getServiceLabel(service)}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaClock className="me-2 text-primary" />
                                                    Expérience minimum
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.yearsOfExperience}
                                                    onChange={(e) => handleFilterChange('yearsOfExperience', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Toutes les expériences</option>
                                                    <option value="0">0 an</option>
                                                    <option value="1">1 an et plus</option>
                                                    <option value="3">3 ans et plus</option>
                                                    <option value="5">5 ans et plus</option>
                                                    <option value="10">10 ans et plus</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12} sm={6} md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">
                                                    <FaStar className="me-2 text-primary" />
                                                    Langue
                                                </Form.Label>
                                                <Form.Select
                                                    value={filters.languages}
                                                    onChange={(e) => handleFilterChange('languages', e.target.value)}
                                                    size="sm"
                                                >
                                                    <option value="">Toutes les langues</option>
                                                    {filterOptions.languages.map(language => (
                                                        <option key={language} value={language}>{language}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </div>

                    {/* ===== RÉSULTATS ===== */}
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-3 text-muted">Chargement des professionnels validés...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">
                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                                <div>
                                    <h6 className="alert-heading">Erreur de chargement</h6>
                                    <p className="mb-0">{error}</p>
                                </div>
                                <Button variant="primary" onClick={fetchValidatedProfessionals}>
                                    Réessayer
                                </Button>
                            </div>
                        </div>
                    ) : filteredProfessionals.length === 0 ? (
                        <div className="text-center py-5">
                            <FaSearch size={48} className="text-secondary mb-4" />
                            <h5 className="text-muted mb-2">
                                {professionals.length === 0 ? 'Aucun professionnel validé disponible' : 'Aucun résultat pour vos critères de recherche'}
                            </h5>
                            <p className="text-muted">
                                {professionals.length === 0
                                    ? "Aucun professionnel n'est actuellement validé et disponible."
                                    : 'Aucun professionnel ne correspond à vos critères de recherche et de filtrage.'}
                            </p>
                            {professionals.length === 0 && (
                                <Button variant="outline-primary" className="rounded-pill px-4" onClick={fetchValidatedProfessionals}>
                                    Actualiser
                                </Button>
                            )}
                            {(searchTerm || activeFiltersCount > 0) && professionals.length > 0 && (
                                <Button
                                    variant="outline-primary"
                                    className="rounded-pill px-4 ms-2"
                                    onClick={resetFilters}
                                >
                                    Réinitialiser les filtres
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <Row className="g-4">
                                {currentProfessionals.map((professional, index) => (
                                    <Col key={professional.id || index} xs={12} md={6} xl={4}>
                                        <Card className="professional-card border-0 rounded-4 shadow-sm h-100">
                                            {/* Photo */}
                                            <div
                                                className="pc-photo-frame rounded-top-4"
                                                style={{ height: `${CARD_IMAGE_HEIGHT}px` }}
                                                onClick={() => handleOpenPhotoPreview(professional.displayPhotoUrl)}
                                            >
                                                <img
                                                    src={professional.displayPhotoUrl}
                                                    alt={`${professional.firstName} ${professional.lastName}`}
                                                    className="pc-photo-img"
                                                    onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
                                                />

                                                <div className="professional-photo-gradient position-absolute top-0 start-0 w-100 h-100" />

                                                <div className="photo-zoom-hint position-absolute top-50 start-50 translate-middle text-white fs-2">
                                                    <FaSearchPlus />
                                                </div>

                                                <div className="position-absolute top-0 end-0 m-3">
                                                    {getStatusBadge(professional.status)}
                                                </div>

                                                <div className="position-absolute top-0 start-0 m-3">
                                                    <span className="pc-experience-badge">
                                                        {professional.yearsOfExperience} ans exp.
                                                    </span>
                                                </div>

                                                <div className="position-absolute bottom-0 start-0 end-0 p-3 text-white">
                                                    <h5 className="text-white fw-bold mb-0 text-shadow">
                                                        {professional.firstName} {professional.lastName}
                                                    </h5>
                                                    <p className="text-white-50 small mb-0">
                                                        {getServiceLabel(professional.selectedService)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Infos */}
                                            <Card.Body className="d-flex flex-column">
                                                <div className="d-flex flex-column gap-2 mb-3">
                                                    <div className="professional-meta">
                                                        <FaMapMarkerAlt />
                                                        <span>
                                                            {professional.city}
                                                            {professional.neighborhood && professional.neighborhood !== 'Non spécifié' && `, ${professional.neighborhood}`}
                                                            {professional.country && `, ${professional.country}`}
                                                        </span>
                                                    </div>

                                                    <div className="professional-meta">
                                                        <FaClock />
                                                        <span>{professional.yearsOfExperience} ans d'expérience</span>
                                                    </div>

                                                    <div className="professional-meta">
                                                        <FaStar />
                                                        <span>
                                                            {professional.languages
                                                                ? professional.languages.split(/[,;]/).map(l => l.trim()).filter(Boolean).join(', ')
                                                                : 'Non spécifié'}
                                                        </span>
                                                    </div>

                                                    {professional.subServices && renderSubServices(professional.subServices)}
                                                </div>

                                                <div className="professional-actions d-flex gap-2 mt-auto">
                                                    <Button
                                                        variant="outline-primary"
                                                        className="flex-grow-1 rounded-pill"
                                                        onClick={() => handleViewDetails(professional)}
                                                    >
                                                        <FaEye className="me-1" /> Voir plus
                                                    </Button>
                                                    <Button
                                                        variant="primary"
                                                        className="flex-grow-1 rounded-pill"
                                                        onClick={() => handleShowInterest(professional)}
                                                        disabled={!professional.availabilityStatus}
                                                    >
                                                        <FaHandshake className="me-1" />
                                                        {professional.availabilityStatus ? 'Contacter' : 'Indisponible'}
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pc-pagination-wrapper d-flex flex-wrap justify-content-center align-items-center mt-5">
                                    <Pagination className="pagination-custom flex-wrap justify-content-center mb-0">
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        />
                                        {Array.from({ length: totalPages }, (_, i) => {
                                            const pageNumber = i + 1;
                                            const showPage =
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

                                            if (!showPage && (pageNumber === currentPage - 2 || pageNumber === currentPage + 2)) {
                                                return <Pagination.Ellipsis key={`ellipsis-${pageNumber}`} disabled />;
                                            }

                                            return showPage ? (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={pageNumber === currentPage}
                                                    onClick={() => setCurrentPage(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </Pagination.Item>
                                            ) : null;
                                        })}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        />
                                    </Pagination>

                                    <small className="pc-pagination-count ms-3">
                                        {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredProfessionals.length)} sur {filteredProfessionals.length} professionnel(s)
                                    </small>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Modal de détails */}
            {isModalOpen && selectedProfessional && (
                <ProfessionalCandidateDetailModal
                    user={selectedProfessional}
                    show={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            {/* Modal pour la manifestation d'intérêt */}
            <ClientEOICreateModal
                show={showInterestModal}
                onHide={() => setShowInterestModal(false)}
                professional={selectedProfessionalForContact}
                onSuccess={handleInterestSuccess}
            />

            {/* Aperçu agrandi de la photo de profil avec Lightbox et Zoom */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
            />
        </>
    );
};

export default ProfessionalCandidatePage;